// Settings → ORACLE. Every choice made during the first-run Spine (setup) is
// editable here, one tab per onboarding data section. Sectors (TELOS) and
// Gears (MoyRi) are stored in user_settings.spine, so they re-open their real
// setup screens and overwrite on save. SHAPES/Projects live in their tools.
import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { supabase } from '../../lib/supabase';
import { ThemedSwitch } from './ThemedSwitch';
import { useTheme, makeThemedStyles, DISPLAY_FONT } from '../../v10/theme';
import { SectionTitle, Chip, Field, GoldButton, GlassCard } from '../../v10/ui';
import {
  SECTORS,
  SECTOR_LABELS,
  GEARS,
  GEAR_META,
  type Sector,
  type Gear,
} from '../../domain/olmps';
import { loadSpine } from '../spine-data';
import type { SpineState } from '../spine-types';
import { SHAPE_CASCADE } from '../shapeCascade';
import { getTool, type DmtToolId } from '../spectrum-tools';
import { SpineTelos } from '../SpineTelos';
import { GearIntakeFlow } from '../GearIntakeFlow';
import { buildCreatePayload } from '../../tools/TkneTool';
import { useCompassConfig, builtinSpokes, RING_SYSTEMS, RING_SYSTEM_LABEL, type RingSystemId } from '../sectorConfig';
import { SectorCompassPreview } from '../SectorCompassPreview';

type Tab = 'sectors' | 'gears' | 'shapes' | 'projects';
const TABS: { id: Tab; label: string }[] = [
  { id: 'sectors', label: 'Sectors' },
  { id: 'gears', label: 'Gears' },
  { id: 'shapes', label: 'SHAPES' },
  { id: 'projects', label: 'Projects' },
];

export default function OlmpsSettingsEditor({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const { t } = useTheme();
  const [tab, setTab] = useState<Tab>('sectors');
  const [spine, setSpine] = useState<SpineState | null>(null);
  const [editSector, setEditSector] = useState<Sector | null>(null);
  const [editGear, setEditGear] = useState<Gear | null>(null);

  const reload = useCallback(async () => {
    if (!userId) return;
    setSpine(await loadSpine(supabase, userId));
  }, [userId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Re-run a single sector's TELOS setup, then return to the list.
  if (editSector) {
    return (
      <View style={styles.editWrap}>
        <Pressable onPress={() => setEditSector(null)} style={styles.editBack}>
          <Text style={styles.editBackText}>‹ Done editing</Text>
        </Pressable>
        <SpineTelos
          key={editSector}
          userId={userId}
          sector={editSector}
          onDone={() => {
            setEditSector(null);
            void reload();
          }}
        />
      </View>
    );
  }

  if (editGear) {
    return (
      <View style={styles.editWrap}>
        <Pressable onPress={() => setEditGear(null)} style={styles.editBack}>
          <Text style={styles.editBackText}>‹ Done editing</Text>
        </Pressable>
        <GearIntakeFlow
          key={editGear}
          userId={userId}
          gear={editGear}
          onDone={() => {
            setEditGear(null);
            void reload();
          }}
        />
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.tabRow}>
        {TABS.map((t) => (
          <Chip key={t.id} label={t.label} active={tab === t.id} onPress={() => setTab(t.id)} />
        ))}
      </ScrollView>

      <ScrollView contentContainerStyle={styles.body}>
        {spine === null ? (
          <ActivityIndicator color={t.goldBright} style={{ marginTop: 30 }} />
        ) : tab === 'sectors' ? (
          <SectorsTab spine={spine} onEdit={setEditSector} userId={userId} />
        ) : tab === 'gears' ? (
          <GearsTab spine={spine} onEdit={setEditGear} />
        ) : tab === 'shapes' ? (
          <ShapesTab userId={userId} shapesListId={spine.kyrs.shapesListId} />
        ) : (
          <ProjectsTab userId={userId} />
        )}
      </ScrollView>
    </View>
  );
}

function SectorsTab({
  spine,
  onEdit,
  userId,
}: {
  spine: SpineState;
  onEdit: (s: Sector) => void;
  userId: string | undefined;
}) {
  const styles = useStyles();
  const {
    config,
    spokesFor,
    isEnabled,
    toggleBuiltin,
    addCustom,
    renameCustom,
    removeCustom,
    setShapeEnabled,
  } = useCompassConfig(userId);
  const [newLabel, setNewLabel] = useState('');
  const [sysTab, setSysTab] = useState<RingSystemId>('shape');

  const isOn = (sector: Sector) => isEnabled('shape', sector);

  const submitCustom = () => {
    const t = newLabel.trim();
    if (!t) return;
    addCustom(sysTab, t);
    setNewLabel('');
  };

  return (
    <View style={styles.list}>
      {/* Master SHAPE-ring switch: off by default until ASTRA is acquired (or
          switched on manually here). ORDER is always on and has no switch. */}
      <GlassCard style={styles.shapeToggleCard}>
        <View style={styles.flex1}>
          <Text style={styles.cardTitle}>SHAPE ring</Text>
          <Text style={styles.cardEmpty}>Unlocked by acquiring ASTRA — or switch on early.</Text>
        </View>
        <ThemedSwitch value={config.shapeEnabled} onValueChange={setShapeEnabled} />
      </GlassCard>

      {/* System tabs — ORDER (always on) / SHAPE (life-area sectors). */}
      <View style={styles.systemTabs}>
        {RING_SYSTEMS.map((sys) => (
          <Chip key={sys} small label={RING_SYSTEM_LABEL[sys]} active={sysTab === sys} onPress={() => setSysTab(sys)} />
        ))}
      </View>

      {/* Live compass preview for the selected system — spokes update as
          built-ins are toggled / custom ones are added. */}
      <SectorCompassPreview spokes={spokesFor(sysTab)} />

      {sysTab === 'shape' ? (
        <Text style={styles.hint}>
          Toggle a sector off to drop its spoke from the compass, or add your own. Tap a sector to edit its prescribed
          future — Endeavour, deity (guiding skillset), and Aims.
        </Text>
      ) : (
        <Text style={styles.hint}>
          Toggle an ORDER gear off to drop its spoke from the compass, or add your own.
        </Text>
      )}

      {sysTab === 'order'
        ? builtinSpokes('order').map((spoke) => {
            const on = isEnabled('order', spoke.id);
            return (
              <GlassCard key={spoke.id} style={[styles.card, !on && styles.cardOff]}>
                <View style={styles.cardHead}>
                  <View style={[styles.swatch, { backgroundColor: spoke.color }]} />
                  <Text style={[styles.cardTitle, styles.flex1]}>{spoke.label}</Text>
                  <ThemedSwitch value={on} onValueChange={() => toggleBuiltin('order', spoke.id)} />
                </View>
                {!on ? <Text style={styles.cardEmpty}>Hidden — no spoke on the compass</Text> : null}
              </GlassCard>
            );
          })
        : SECTORS.map((sector) => {
            const setup = spine.telos[sector];
            const cascade = SHAPE_CASCADE[sector];
            const deity = cascade.deities.find((d) => d.id === setup?.deityId);
            const on = isOn(sector);
            return (
              <GlassCard key={sector} style={[styles.card, !on && styles.cardOff]}>
                <View style={styles.cardHead}>
                  <Pressable style={styles.flex1} onPress={() => onEdit(sector)}>
                    <Text style={styles.cardTitle}>{SECTOR_LABELS[sector]}</Text>
                  </Pressable>
                  <Pressable onPress={() => onEdit(sector)}>
                    <Text style={styles.editLink}>{setup?.done ? 'Change ›' : 'Set up ›'}</Text>
                  </Pressable>
                  <ThemedSwitch value={on} onValueChange={() => toggleBuiltin('shape', sector)} />
                </View>
                {!on ? (
                  <Text style={styles.cardEmpty}>Hidden — no spoke on the compass</Text>
                ) : setup?.done ? (
                  <>
                    {!!setup.visionText && <Text style={styles.cardVision}>{setup.visionText}</Text>}
                    <Text style={styles.cardMeta}>
                      {deity ? `${deity.habit} (${deity.name}) · L${setup.deityLevel}` : 'No skillset set'}
                      {setup.aims.length ? ` · ${setup.aims.length} aim${setup.aims.length > 1 ? 's' : ''}` : ''}
                    </Text>
                  </>
                ) : (
                  <Text style={styles.cardEmpty}>Not set yet</Text>
                )}
              </GlassCard>
            );
          })}

      {/* Custom spokes for the selected system — extra spokes with no
          prescribed-future data yet. */}
      {config.systems[sysTab].custom.length > 0 ? <SectionTitle style={styles.sectionGap}>Custom spokes</SectionTitle> : null}
      {config.systems[sysTab].custom.map((cs) => (
        <GlassCard key={cs.id} style={styles.card}>
          <View style={styles.rowLine}>
            <View style={[styles.swatch, { backgroundColor: cs.color }]} />
            <Field style={styles.flex1}
              inputStyle={[styles.input, styles.flex1]}
              defaultValue={cs.label}
              placeholder="Spoke name"
              onEndEditing={(e) => {
                const t = e.nativeEvent.text.trim();
                if (t && t !== cs.label) renameCustom(sysTab, cs.id, t);
              }}
            />
            <Pressable style={styles.deleteBtn} onPress={() => removeCustom(sysTab, cs.id)}>
              <Text style={styles.deleteText}>✕</Text>
            </Pressable>
          </View>
        </GlassCard>
      ))}

      <SectionTitle style={styles.sectionGap}>Add a custom spoke</SectionTitle>
      <GlassCard style={styles.card}>
        <Field
          inputStyle={styles.input}
          value={newLabel}
          onChangeText={setNewLabel}
          placeholder="e.g. Spiritual"
          onSubmitEditing={submitCustom}
          returnKeyType="done"
        />
        <GoldButton label="+ Add spoke" onPress={() => {
            if (!newLabel.trim()) return;
            void submitCustom();
          }} style={[styles.actionGap, !newLabel.trim() && styles.actionButtonDisabled]} />
      </GlassCard>
    </View>
  );
}

function GearsTab({ spine, onEdit }: { spine: SpineState; onEdit: (g: Gear) => void }) {
  const styles = useStyles();
  return (
    <View style={styles.list}>
      <Text style={styles.hint}>The most-active malady each gear is working against, from your MoyRi intake.</Text>
      {GEARS.map((gear) => {
        const res = spine.moyri[gear];
        let line = 'Not diagnosed yet';
        if (res) {
          const tool = getTool(res.toolId as DmtToolId);
          const malady = tool.maladies.find((m) => m.id === res.subtype)?.name ?? tool.maladies[0]?.name ?? '';
          line = `${malady} vs. ${tool.name}`;
        }
        return (
          <Pressable key={gear} onPress={() => onEdit(gear)}>
            <GlassCard style={styles.card}>
              <View style={styles.cardHead}>
                <Text style={styles.cardTitle}>{GEAR_META[gear].name}</Text>
                <Text style={styles.editLink}>{res ? 'Redo ›' : 'Diagnose ›'}</Text>
              </View>
              <Text style={styles.cardMeta}>{line}</Text>
            </GlassCard>
          </Pressable>
        );
      })}
    </View>
  );
}

// A tappable row of sector chips (S/H/A/P/E). `value` may be null (unset).
// Tapping the active chip again clears it back to null.
function SectorChips({ value, onPick }: { value: Sector | null; onPick: (s: Sector | null) => void }) {
  const styles = useStyles();
  return (
    <View style={styles.chipRow}>
      {SECTORS.map((s) => {
        const active = value === s;
        return (
          <Chip key={s} label={SECTOR_LABELS[s]} active={active} onPress={() => onPick(active ? null : s)} />
        );
      })}
    </View>
  );
}

// ---- SHAPES tab: the recurring actions in the KYRS "SHAPES" task list. Each
// action is a plain `tasks` row (list_id = the SHAPES list). Add / rename /
// re-sector / reschedule / delete them inline, without leaving Settings.
interface ShapeRow {
  id: string;
  title: string;
  category: string | null;
  scheduled: string | null;
  scheduled_manual?: boolean;
  done: boolean;
}

function ShapesTab({ userId, shapesListId }: { userId: string | undefined; shapesListId: string | null }) {
  const styles = useStyles();
  const { t } = useTheme();
  const [rows, setRows] = useState<ShapeRow[] | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newSector, setNewSector] = useState<Sector>('S');

  const load = useCallback(async () => {
    if (!userId || !shapesListId) {
      setRows([]);
      return;
    }
    const { data } = await supabase
      .from('tasks')
      .select('id, title, category, scheduled, done')
      .eq('user_id', userId)
      .eq('list_id', shapesListId)
      .order('order', { ascending: true })
      .order('created_at', { ascending: true });
    setRows((data as ShapeRow[]) ?? []);
  }, [userId, shapesListId]);

  useEffect(() => {
    void load();
  }, [load]);

  const patchRow = async (id: string, patch: Partial<ShapeRow>) => {
    if (!userId) return;
    setRows((prev) => prev?.map((r) => (r.id === id ? { ...r, ...patch } : r)) ?? prev);
    await supabase
      .from('tasks')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', userId);
  };

  const addRow = async () => {
    const title = newTitle.trim();
    if (!userId || !shapesListId || !title) return;
    const { data } = await supabase
      .from('tasks')
      .insert({
        user_id: userId,
        list_id: shapesListId,
        title,
        category: SECTOR_LABELS[newSector],
        done: false,
      })
      .select('id, title, category, scheduled, done')
      .single();
    setNewTitle('');
    if (data) setRows((prev) => [...(prev ?? []), data as ShapeRow]);
  };

  const deleteRow = (row: ShapeRow) => {
    Alert.alert('Remove action?', `"${row.title}" will be deleted from your SHAPES list.`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          if (!userId) return;
          setRows((prev) => prev?.filter((r) => r.id !== row.id) ?? prev);
          await supabase.from('tasks').delete().eq('id', row.id).eq('user_id', userId);
        },
      },
    ]);
  };

  // Map a stored category label (e.g. "Health") back to its Sector key for chips.
  const sectorOf = (category: string | null): Sector | null =>
    SECTORS.find((s) => SECTOR_LABELS[s] === category) ?? null;

  if (rows === null) {
    return <ActivityIndicator color={t.goldBright} style={{ marginTop: 30 }} />;
  }

  if (!shapesListId) {
    return (
      <View style={styles.list}>
        <GlassCard style={styles.card}>
          <Text style={styles.cardTitle}>No SHAPES list yet</Text>
          <Text style={styles.cardVision}>
            Finish the KYRS step in setup to create your SHAPES list, then your recurring actions show up here.
          </Text>
        </GlassCard>
      </View>
    );
  }

  return (
    <View style={styles.list}>
      <Text style={styles.hint}>Your recurring actions. Rename, re-sector, reschedule, or remove them.</Text>

      {rows.length === 0 ? <Text style={styles.cardEmpty}>No actions yet — add one below.</Text> : null}

      {rows.map((row) => (
        <GlassCard key={row.id} style={styles.card}>
          <View style={styles.rowLine}>
            <Field style={styles.flex1}
              inputStyle={[styles.input, styles.flex1]}
              defaultValue={row.title}
              placeholder="Action"
              onEndEditing={(e) => {
                const t = e.nativeEvent.text.trim();
                if (t && t !== row.title) void patchRow(row.id, { title: t });
              }}
            />
            <Pressable style={styles.deleteBtn} onPress={() => deleteRow(row)}>
              <Text style={styles.deleteText}>✕</Text>
            </Pressable>
          </View>
          <SectorChips
            value={sectorOf(row.category)}
            onPick={(s) => void patchRow(row.id, { category: s ? SECTOR_LABELS[s] : null })}
          />
          <View style={styles.rowLine}>
            <Field style={styles.flex1}
              inputStyle={[styles.input, styles.flex1]}
              defaultValue={row.scheduled ?? ''}
              placeholder="Schedule (YYYY-MM-DD, optional)"
              autoCapitalize="none"
              onEndEditing={(e) => {
                const v = e.nativeEvent.text.trim();
                if (v === (row.scheduled ?? '')) return;
                void patchRow(row.id, { scheduled: v || null, scheduled_manual: !!v });
              }}
            />
          </View>
        </GlassCard>
      ))}

      <SectionTitle style={styles.sectionGap}>Add an action</SectionTitle>
      <GlassCard style={styles.card}>
        <Field
          inputStyle={styles.input}
          value={newTitle}
          onChangeText={setNewTitle}
          placeholder="e.g. 10-minute walk"
        />
        <SectorChips value={newSector} onPick={(s) => setNewSector(s ?? newSector)} />
        <GoldButton label="+ Add to SHAPES" onPress={() => {
            if (!newTitle.trim()) return;
            void addRow();
          }} style={[styles.actionGap, !newTitle.trim() && styles.actionButtonDisabled]} />
      </GlassCard>
    </View>
  );
}

// ---- Projects tab: `designs` rows. A project shows on ASTRA only
// when it is active AND has a sector. Add / rename / re-sector / activate /
// delete inline. Full project detail still lives in the TKNE tool.
interface ProjectRow {
  id: string;
  project_title: string | null;
  sector: string | null;
  is_active: boolean | null;
}

function ProjectsTab({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const { t } = useTheme();
  const [rows, setRows] = useState<ProjectRow[] | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!userId) {
      setRows([]);
      return;
    }
    const { data } = await supabase
      .from('designs')
      .select('id, project_title, sector, is_active')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });
    setRows((data as ProjectRow[]) ?? []);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const patchRow = async (id: string, patch: Partial<ProjectRow>) => {
    if (!userId) return;
    setRows((prev) => prev?.map((r) => (r.id === id ? { ...r, ...patch } : r)) ?? prev);
    await supabase
      .from('designs')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', userId);
  };

  const addProject = async () => {
    const title = newTitle.trim();
    if (!userId || !title || busy) return;
    setBusy(true);
    const payload = { ...buildCreatePayload(userId), project_title: title };
    const { data } = await supabase
      .from('designs')
      .insert(payload)
      .select('id, project_title, sector, is_active')
      .single();
    setBusy(false);
    setNewTitle('');
    if (data) setRows((prev) => [data as ProjectRow, ...(prev ?? [])]);
  };

  const deleteProject = (row: ProjectRow) => {
    Alert.alert('Delete project?', `"${row.project_title || 'Untitled project'}" will be permanently deleted.`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          if (!userId) return;
          setRows((prev) => prev?.filter((r) => r.id !== row.id) ?? prev);
          await supabase.from('designs').delete().eq('id', row.id).eq('user_id', userId);
        },
      },
    ]);
  };

  const isSector = (v: string | null): v is Sector => !!v && (SECTORS as string[]).includes(v);

  if (rows === null) {
    return <ActivityIndicator color={t.goldBright} style={{ marginTop: 30 }} />;
  }

  return (
    <View style={styles.list}>
      <Text style={styles.hint}>
        A project shows on your ASTRA when it&apos;s active and has a sector.
      </Text>

      {rows.length === 0 ? <Text style={styles.cardEmpty}>No projects yet — add one below.</Text> : null}

      {rows.map((row) => (
        <GlassCard key={row.id} style={styles.card}>
          <View style={styles.rowLine}>
            <Field style={styles.flex1}
              inputStyle={[styles.input, styles.flex1]}
              defaultValue={row.project_title ?? ''}
              placeholder="Project name"
              onEndEditing={(e) => {
                const t = e.nativeEvent.text.trim();
                if (t !== (row.project_title ?? '')) void patchRow(row.id, { project_title: t });
              }}
            />
            <Pressable style={styles.deleteBtn} onPress={() => deleteProject(row)}>
              <Text style={styles.deleteText}>✕</Text>
            </Pressable>
          </View>
          <SectorChips
            value={isSector(row.sector) ? row.sector : null}
            onPick={(s) => void patchRow(row.id, { sector: s })}
          />
          <Pressable
            style={styles.activeToggle}
            onPress={() => void patchRow(row.id, { is_active: !row.is_active })}
          >
            <Text style={[styles.activeToggleText, !row.is_active && styles.activeToggleTextOff]}>
              {row.is_active ? '● Active — on ASTRA' : '○ Inactive — hidden'}
            </Text>
          </Pressable>
        </GlassCard>
      ))}

      <SectionTitle style={styles.sectionGap}>Add a project</SectionTitle>
      <GlassCard style={styles.card}>
        <Field
          inputStyle={styles.input}
          value={newTitle}
          onChangeText={setNewTitle}
          placeholder="Project name"
        />
        <GoldButton label={busy ? 'Adding…' : '+ Add project'} onPress={() => {
            if (!newTitle.trim() || busy) return;
            void addProject();
          }} style={[styles.actionGap, (!newTitle.trim() || busy) && styles.actionButtonDisabled]} />
      </GlassCard>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, neu, radii }) =>
  StyleSheet.create({
  actionGap: { marginTop: 8 },
  sectionGap: { marginTop: 18 },
  root: { flex: 1 },
  editWrap: { flex: 1, minHeight: 480 },
  editBack: { paddingVertical: 10, paddingHorizontal: 16 },
  editBackText: { fontSize: 15, fontWeight: '700', color: t.goldBright },
  tabRow: { gap: 8, paddingHorizontal: 16, paddingVertical: 10 },
  body: { paddingHorizontal: 16, paddingBottom: 40 },
  shapeToggleCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  systemTabs: { flexDirection: 'row', gap: 8 },
  list: { gap: 10 },
  hint: { fontSize: 12, color: t.ivorySoft, fontStyle: 'italic', marginBottom: 2 },
  card: {
    paddingHorizontal: 14,
    paddingVertical: 12,
    gap: 4,
  },
  cardOff: { opacity: 0.55 },
  cardHead: { flexDirection: 'row', alignItems: 'center', gap: 10, justifyContent: 'space-between' },
  swatch: { width: 18, height: 18, borderRadius: 5 },
  cardTitle: { fontSize: 15, fontWeight: '800', color: t.ivory, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  editLink: { fontSize: 13, fontWeight: '800', color: t.goldBright },
  cardVision: { fontSize: 13, color: t.ivorySoft, lineHeight: 19 },
  cardMeta: { fontSize: 12, fontWeight: '600', color: t.goldBright },
  cardEmpty: { fontSize: 13, color: t.ivorySoft, fontStyle: 'italic', marginVertical: 4 },
  rowLine: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  flex1: { flex: 1 },
  input: {
  },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  deleteBtn: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  deleteText: { color: t.lapsed, fontWeight: '900' },
  actionButtonDisabled: { opacity: 0.4 },
  activeToggle: { paddingVertical: 4 },
  activeToggleText: { fontSize: 12, fontWeight: '700', color: t.goldBright },
  activeToggleTextOff: { color: t.ivorySoft },
}),
);
